import React, { useState, useRef, useEffect, useCallback } from 'react';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import debounce from 'lodash.debounce';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import AsyncSelect from 'react-select/async';
import Select from 'react-select/dist/declarations/src/Select';
import { StylesConfig } from 'react-select/dist/declarations/src';
import { hasOnlySuggestions } from 'components/search/useFormatGroupLabel';
import { components } from 'react-select';

import { Modal, ModalSize } from '../../../components/modal/Modal';
import { FormikTextField } from '../../../components/fields/FormikTextField';
import { createStyles } from 'utils/createStyle';
import { suggestBrands } from 'common/api/search/suggest/getSuggestions';
import { includesEq } from 'common/utils/searchFilters';
import { SupportedType } from 'common/api/search/models/SupportedType';
import { colors } from 'styles/settings';
import {
    walletCardNumberValidator,
    walletCardPinValidator,
    walletCardNameValidator,
} from 'common/modules/form-validation/validators';
import { useRootSelector } from 'common/features/featuresReducer';
import { registerCard, validateCard } from 'common/api/wallet/cards';
import { getReloadProductThunk, upsertCard } from 'common/features/store/duck/wallet/duck';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import { delay } from 'common/utils';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { GroupBase, OptionProps } from 'react-select';
import { highlightMatchesHtml } from 'utils';
import { getProductsByBrand } from 'common/api/e-comm/brands';
import { isPinRequiredForReloadOrBalance } from 'common/api/wallet/cards';
import { Product } from 'common/api/e-comm/models/Product';

interface AddNewCardModalProps {
    isOpen?: boolean;
    closeModal: () => void;
}

interface AddCardFormValues {
    brand: string;
    number: string;
    pin?: string;
    name?: string;
}

const addCardFormInitialValues = {
    brand: '',
    number: '',
    pin: '',
    name: '',
};

const styles = createStyles({
    submitButton: [
        'rounded-full bg-brand text-white font-semibold text-lg py-2 mb-2 w-full',
        { ':disabled': { backgroundColor: colors.greyAccent, color: colors.grey4 } },
    ],
    textInput: [
        'bg-grey-accent',
        {
            border: 'none',
            minWidth: 275,
            '&::-webkit-calendar-picker-indicator': { display: 'none !important' },
            '&::-webkit-list-button': { display: 'none !important' },
        },
    ],
});

const RetryAddingCard = ({
    attempts,
    isSubmitting,
}: {
    attempts: number;
    isSubmitting: boolean;
}) => {
    return (
        <div>
            {attempts > 0 && attempts < 3 && (
                <button
                    disabled={isSubmitting}
                    type="submit"
                    className={classnames(styles.submitButton, 'w-full flex justify-center')}
                >
                    {!isSubmitting ? (
                        <span>Retry</span>
                    ) : (
                        <span>
                            <ActivityIndicator width="30" />
                        </span>
                    )}
                </button>
            )}
        </div>
    );
};

const AddCardModal = ({ isOpen, closeModal }: AddNewCardModalProps) => {
    const dispatch = useDispatch<any>();
    const submitAttempts = useRef<number>(0);
    const [formError, setFormError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const { userId } = useRootSelector((s) => s.store.account);

    const onSubmit = async (
        values: AddCardFormValues,
        actions: FormikHelpers<AddCardFormValues>
    ) => {
        if (!userId) {
            return;
        }

        actions.setSubmitting(true);
        const selectedBrandId = parseInt(values.brand, 10);
        const errMsg =
            submitAttempts.current > 1
                ? 'Unable to add card. Please try again later.'
                : 'Unable to add card.';

        if (!selectedBrandId) {
            actions.setSubmitting(false);
            setFormError(errMsg);
            return;
        }

        try {
            const reloadProduct = await getReloadProductThunk(selectedBrandId)(dispatch);

            if (reloadProduct.error || !reloadProduct.data) {
                actions.setSubmitting(false);
                submitAttempts.current += 1;
                setFormError(errMsg);
                return;
            }

            const validCardResponse = await validateCard(
                values.number,
                reloadProduct.data.supplierCode
            );

            if (validCardResponse.data?.cardEligibilityType === 'InstantActivate') {
                actions.setFieldError(
                    'number',
                    'This card can only be activated in The RaiseRight App.'
                );
                return;
            }

            const result = await registerCard({
                brandId: selectedBrandId,
                cardNumber: values.number.trim(),
                pin: values.pin || null,
                name: values.name || null,
                productSku: reloadProduct.data.sku,
                supplierCode: reloadProduct.data.supplierCode,
            });

            // add a small delay for better loading responsiveness
            await delay(1000);
            submitAttempts.current += 1;

            if (!result.data) {
                actions.setSubmitting(false);

                setFormError(errMsg);
                return;
            }

            dispatch(upsertCard({ userId, card: result.data }));
            actions.setSubmitting(false);
            setSubmitSuccess(true);
        } catch (err) {
            actions.setSubmitting(false);
            setFormError(errMsg);
        }
    };

    const onClose = () => {
        setFormError('');
        submitAttempts.current = 0;
        closeModal();
        setSubmitSuccess(false);
    };

    return (
        <Modal
            title="Add a New Card"
            isOpen={isOpen}
            onClose={onClose}
            size={[ModalSize.CONTENT_WIDTH, ModalSize.CONTENT_WIDTH]}
        >
            <Formik<AddCardFormValues> initialValues={addCardFormInitialValues} onSubmit={onSubmit}>
                <AddCardForm
                    submitAttempts={submitAttempts.current}
                    onClose={onClose}
                    submitSuccess={submitSuccess}
                    formError={formError}
                />
            </Formik>
        </Modal>
    );
};

const AddCardForm = ({
    submitAttempts,
    onClose,
    submitSuccess,
    formError,
}: {
    submitAttempts: number;
    onClose: () => void;
    submitSuccess: boolean;
    formError: string;
}) => {
    const [isRequired, setIsRequired] = useState(false);
    const [product, setProduct] = useState<Product | null>(null);

    const formikProps = useFormikContext<AddCardFormValues>();
    const brandId = formikProps.values.brand;

    useEffect(() => {
        if (brandId) {
            getProductsByBrand(parseInt(brandId)).then((productResponse) => {
                if (productResponse.data && productResponse.data?.length > 0) {
                    setProduct(productResponse.data[0]);
                }
            });
        }
    }, [brandId]);

    const checkCardValidity = async (cardNumber: string, brandId: string) => {
        const id = parseInt(brandId);
        if (cardNumber.length >= 12 && !isNaN(id) && product) {
            const supplierCode = product.supplierCode;
            const validCardResponse = await validateCard(cardNumber, supplierCode);

            if (
                validCardResponse.data &&
                validCardResponse.data.cardEligibilityType === 'InstantActivate'
            ) {
                formikProps.setFieldError(
                    'number',
                    'This card can only be activated in The RaiseRight App.'
                );
            }
        }
    };

    const supplierCode = product?.supplierCode;
    useEffect(() => {
        if (supplierCode) {
            isPinRequiredForReloadOrBalance(supplierCode).then((result) => {
                setIsRequired(Boolean(result.data?.isPinRequired));
            });
        }
    }, [supplierCode]);

    const validatePin = useCallback(
        (value: string) => {
            if (value.length) {
                return walletCardPinValidator(value);
            } else if (isRequired) {
                return 'Pin is required.';
            }
        },
        [isRequired]
    );

    return (
        <form
            className="flex flex-col w-auto md:w-96"
            noValidate
            autoComplete="off"
            onSubmit={formikProps.handleSubmit}
        >
            {formError && (
                <label className="flex w-72 sm:w-full flex-col h-60 font-semibold items-center justify-center text-2xl mb-4">
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        size="lg"
                        className="text-error mr-4"
                    />
                    <span className="text-center text-brand mt-4">{formError}</span>
                    <span className="text-xl text-sm text-grey-2">
                        Please note, only eligible gift cards purchased through RaiseRight can be
                        added to your wallet.
                    </span>
                </label>
            )}
            {!submitSuccess && (
                <div
                    className={classnames(submitAttempts > 0 ? 'hidden' : 'block')}
                    data-recording-disable
                >
                    <SearchBrandDropdown />
                    <FormikTextField<AddCardFormValues>
                        className={styles.textInput}
                        wrapperStyles="mb-7"
                        placeholder="Enter gift card number"
                        label="Card Number"
                        name="number"
                        disabled={!formikProps.values.brand}
                        validate={walletCardNumberValidator}
                        onBlur={() =>
                            checkCardValidity(formikProps.values.number, formikProps.values.brand)
                        }
                        data-testid="card_number"
                    />
                    <FormikTextField<AddCardFormValues>
                        className={styles.textInput}
                        wrapperStyles="mb-7"
                        placeholder="Enter security pin"
                        label="PIN"
                        name="pin"
                        disabled={!formikProps.values.brand}
                        validate={validatePin}
                    />
                    <FormikTextField<AddCardFormValues>
                        className={styles.textInput}
                        wrapperStyles="mb-12"
                        placeholder="Enter custom card name"
                        label="Name (Optional)"
                        name="name"
                        onChange={() => {
                            formikProps.setFieldTouched('name');
                        }}
                        disabled={!formikProps.values.brand}
                        validate={(value) => {
                            if (value.length) {
                                return walletCardNameValidator(value);
                            }
                        }}
                    />
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={
                            formikProps.isSubmitting ||
                            !formikProps.isValid ||
                            !formikProps.dirty ||
                            submitAttempts > 2 ||
                            submitSuccess
                        }
                        data-testid="add_card_submit_button"
                    >
                        {formikProps.isSubmitting ? 'Adding Card...' : 'Add Card'}
                    </button>
                </div>
            )}
            {submitSuccess ? (
                <React.Fragment>
                    <div
                        style={{ minWidth: 275 }}
                        className="text-success flex flex-col items-center mb-10 py-16"
                    >
                        <FontAwesomeIcon icon={faCheckCircle} size="3x" />
                        <h5
                            className="font-semibold text-2xl text-center mt-8"
                            data-testid="success_message"
                        >
                            Card Successfully Added
                        </h5>
                    </div>
                </React.Fragment>
            ) : (
                <RetryAddingCard
                    attempts={submitAttempts}
                    isSubmitting={formikProps.isSubmitting}
                />
            )}
            <button
                onClick={(evt: React.SyntheticEvent) => {
                    evt.preventDefault();
                    formikProps.resetForm();
                    onClose();
                }}
                className="text-brand font-semibold underline text-lg py-2"
                data-testid="close_button"
            >
                {!submitSuccess && submitAttempts === 0 ? 'Cancel' : 'Close'}
            </button>
        </form>
    );
};

const CustomOption = ({
    innerRef,
    innerProps,
    data,
    isFocused,
    label,
    ...props
}: OptionProps<Brand, false, GroupBase<Brand>>) => {
    // highligh matches
    const filter = props.selectProps.inputValue?.toLowerCase() as string;
    const highlighted = highlightMatchesHtml(label, filter);

    return (
        <div
            {...innerProps}
            data-testid={data.name}
            ref={innerRef}
            key={data.id || 0}
            className={`${
                isFocused
                    ? 'border-l-4 border-solid border-brand '
                    : 'border-l-4 border-solid border-white '
            }px-4 py-2 no-underline capitalize text-left text-brand hover:underline focus:underline grid`}
            style={{ gridTemplateColumns: 'max-content auto' }}
            title={label}
        >
            {data.imageUrl ? (
                <img src={data.imageUrl} className="inline-block w-16" />
            ) : (
                renderEmpty(data)
            )}
            <a
                href="#"
                className={'no-underline align-top ml-2 hover:underline leading-10 truncate w-full'}
                onClick={(e) => e.preventDefault()}
            >
                {highlighted}
            </a>
        </div>
    );
};

const renderEmpty = (data: Brand) => {
    if (Number(data.id) <= 0) {
        return <div />;
    }

    return <div className="inline-block w-16 bg-grey-3" />;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Input = (props: any) => {
    return (
        <components.Input
            data-testid="brand_name"
            {...props}
            className={`${0} text-grey-1 border-0`}
            isHidden={false}
        />
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SingleValue = (props: any) => {
    const children = props.selectProps.menuIsOpen ? null : props.children;
    return <components.SingleValue {...props}>{children}</components.SingleValue>;
};

const SearchBrandDropdown = () => {
    const formikProps = useFormikContext();
    const ref = useRef<Select<Brand, false, GroupBase<Brand>>>(null);
    const [term, applyTerm] = useState('');
    const st: StylesConfig<Brand, boolean, GroupBase<Brand>> = {
        control: (base) => ({ ...base, border: 0, background: '#f7f7f7' }),
        placeholder: (base) => ({
            ...base,
            color: '#939598 !important',
        }),
        singleValue: (base) => ({ ...base, color: '#58595b !important' }),
        menu: (base) => ({ ...base, marginTop: 3 }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadOptions = useCallback(
        debounce((searchString: string, callback: Function) => {
            // using the store object to guarantee that we have the latest state.
            const promise = suggestBrands({
                search: `${searchString}`,
                filter: includesEq('SupportedTypes', SupportedType.Reloadable),
                select: 'Id, Name, ImageUrl',
            });

            // waits the request complete, and reducers to run to get the filteredResults from the store.
            promise.then((result) => {
                const value = result.data || [];
                const onlySuggestions = hasOnlySuggestions(
                    searchString,
                    value as unknown as Brand[]
                );

                if (value.length <= 0) {
                    return callback([]);
                }

                const results = onlySuggestions
                    ? [
                          {
                              label: '',
                              options: value,
                          },
                      ]
                    : [
                          {
                              label: '',
                              options: [...value],
                          },
                      ];

                return callback(results);
            });
        }, 500),
        []
    );

    const onInputChange = (input: string) => {
        applyTerm(input);

        return input;
    };

    return (
        <div className="pb-7">
            <label className="text-grey-1 font-bold mb-1 block text-xs">Brand</label>
            <AsyncSelect
                data-testid="add-card-dropdown"
                ref={ref}
                styles={st}
                getOptionValue={(item) => item.id}
                getOptionLabel={(item) => item.name}
                className="py-1w-full max-w-xs sm:max-w-lg text-left"
                inputValue={term}
                isClearable={false}
                placeholder="Search for gift-card brand"
                noOptionsMessage={(_) =>
                    !_.inputValue
                        ? 'Type something to search for Brands.'
                        : `No brands were found with filter: ${_.inputValue}`
                }
                loadingMessage={(_) => `Searching brands: ${_.inputValue}...`}
                loadOptions={loadOptions}
                components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                    Option: CustomOption,
                    Input,
                    SingleValue,
                }}
                onInputChange={(newValue) =>
                    newValue.length <= 75 ? onInputChange(newValue) : newValue
                }
                onChange={(item) => {
                    const itemSelected = item as Brand;
                    if (itemSelected?.id) {
                        formikProps.setFieldValue('brand', itemSelected.id);
                        setTimeout(() => {
                            ref.current?.blur();
                            ref.current?.focus();
                            ref.current?.blur();
                        }, 500);
                    }
                }}
                // using fixed to avoid issues with overflow: hidden|auto etc..
                menuPosition="fixed"
            />
        </div>
    );
};

export default AddCardModal;
